import { useQuery } from "@tanstack/react-query";
import { useDeferredValue, useMemo, useState } from "react";
import { PageContainer } from "../../../components/core/basic/PageContainer";
import BreadcrumbDates from "../../../components/core/blocks/BreadcrumbDates";
import MetricsCard from "../../../components/custom/merchandise/MetricsCard";
import { usePersistedTableState } from "../../../components/table/usePersistedTableState";
import { idsToVisibilityState, selectionStateToSelectedIds } from "../../../components/table/utils";
import { DatesProvider, useDates } from "../../../dates/DatesContext";
import { useAppSelector } from "../../../redux/hooks";
import { useModuleEnabled } from "../../../redux/selectors/modules";
import { useAcosOrRoas } from "../../../utils/acosOrRoas";
import { ORG_SUMMARY_REPORT_BREADCRUMB_SPEC, REPORT_LINKS } from "../../../utils/dashboardLinks";
import { buildMetric } from "../../../views/merchandise/metrics";
import { COMPARE_METRIC_LOOKUP } from "../../../views/merchandise/tables/metricsByEntity";
import { useGetV4AdvReportByDate } from "../api/v4AdvReportByDate";
import { useGetV4AdvReportTotals } from "../api/v4AdvReportTotals";
import { useStandardEntityMetrics } from "../../../views/merchandise/tables/metricUtils";
import { AdReportTable } from "./AdReportTable";
import useAdReportColumns from "./useAdReportColumns";

function AdvertisingReportBody() {
  const acosOrRoas = useAcosOrRoas();
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const standardMetricIds = useStandardEntityMetrics("mediaPlan");
  const [quickFilterValue, setQuickFilterValue] = useState("all");
  const [adType, setAdType] = useState<string | number | null>("all");
  const columns = useAdReportColumns();

  const { start, end, group } = useDates();
  const defaultSelectedColumns = [
    "state",
    "created_datetime",
    "ad_sales",
    "ad_spend",
    "acos",
    "roas",
    "orders",
    "impressions",
    "clicks",
    "ctr",
    "conversion_rate",
    "cpc",
  ];
  const defaultHiddenCols = idsToVisibilityState(
    defaultSelectedColumns,
    columns.filter((column) => Boolean(column.id)).map((column) => column.id!),
  );
  const { setters, state, initialState } = usePersistedTableState({
    persistKey: "adReport",
    initialState: {
      columnVisibility: {
        ...defaultHiddenCols,
        acos: acosOrRoas(true, false),
        roas: acosOrRoas(false, true),
      },
      columnOrder: defaultSelectedColumns,
      columnPinning: {
        left: ["ad_name"],
      },
      sorting: [{ id: "ad_sales", desc: true }],
    },
  });
  const deferredSearch = useDeferredValue(state.globalFilter);
  const selectedIds = selectionStateToSelectedIds(state.rowSelection);

  const filters = useMemo(() => {
    const selectedIdsFilter =
      selectedIds.length > 0 ? [{ id: "ad_id", value: { op: "anyof", value: selectedIds } }] : [];
    const adTypeFilter = adType !== "all" ? [{ id: "ad_type", value: { op: "=", value: adType } }] : [];
    const quickFilter = quickFilterValue !== "all" ? [{ id: quickFilterValue, value: { op: "=", value: true } }] : [];
    return [...state.columnFilters, ...selectedIdsFilter, ...adTypeFilter, ...quickFilter];
  }, [state.columnFilters, selectedIds, adType, quickFilterValue]);

  const byDateOptionsGetter = useGetV4AdvReportByDate();
  const advByDateReportQuery = useQuery(
    byDateOptionsGetter({
      filters: filters,
      search: deferredSearch,
    }),
  );
  const totalsOptionsGetter = useGetV4AdvReportTotals();
  const advTotalsReportQuery = useQuery(
    totalsOptionsGetter({
      filters: filters,
      search: deferredSearch,
    }),
  );

  const timeLineData = useMemo(() => {
    return advByDateReportQuery.data?.data.map((row) => ({ ...row, name: row.start_date })) || [];
  }, [advByDateReportQuery.data?.data]);

  const metrics = useMemo(() => {
    const data = {
      total: advTotalsReportQuery.data?.data,
    };
    return standardMetricIds.map((id) => buildMetric(marketPlace, data, id, COMPARE_METRIC_LOOKUP[id]));
  }, [advTotalsReportQuery.data?.data, marketPlace, standardMetricIds]);

  return (
    <PageContainer withBreadcrumb>
      <MetricsCard
        id={"adReport"}
        title={"Performance Overview"}
        data={timeLineData}
        loading={
          advByDateReportQuery.isFetching && (!advByDateReportQuery.data || advByDateReportQuery.isPlaceholderData)
        }
        showLoadingSpinner
        showSkeletonLoadingEffect={
          advTotalsReportQuery.isFetching && (!advTotalsReportQuery.data || advTotalsReportQuery.isPlaceholderData)
        }
        metrics={metrics}
        showMetricsSelector
        initialSelectedMetric={["ad_sales", "ad_spend", acosOrRoas("acos", "roas")]}
        group={group}
        showAnnotations={true}
        byline={
          <>
            {start.format("MMMM Do YYYY")}
            {" - "}
            {end.format("MMMM Do YYYY")}
          </>
        }
        context={"adReport"}
      >
        <AdReportTable
          state={state}
          initialState={initialState}
          setters={setters}
          quickFilterValue={quickFilterValue}
          setQuickFilterValue={setQuickFilterValue}
          adType={adType}
          setAdType={setAdType}
          additionalFilters={{
            adType: String(adType),
          }}
          columns={columns}
        />
      </MetricsCard>
    </PageContainer>
  );
}

export default function AdvertisingReport() {
  const hasOrgSummaryreportModule = useModuleEnabled("ORG_SUMMARY_REPORT");
  const breadCrumbItems = [
    {
      name: "Advertisement Report",
      href: "/user/report/advertisement",
      dropdownLinkOptions: REPORT_LINKS,
      searchable: false,
      dropdownSubtitle: "Reports",
    },
  ];

  if (
    hasOrgSummaryreportModule &&
    !breadCrumbItems[0]?.dropdownLinkOptions.find((o) => o.name === ORG_SUMMARY_REPORT_BREADCRUMB_SPEC.name)
  ) {
    breadCrumbItems[0]?.dropdownLinkOptions.unshift(ORG_SUMMARY_REPORT_BREADCRUMB_SPEC);
  }
  return (
    <DatesProvider>
      <BreadcrumbDates items={breadCrumbItems} />
      <AdvertisingReportBody />
    </DatesProvider>
  );
}
